import React, { ReactElement, useEffect } from "react"
import { FormLabel, TextArea, TextInput } from "@wfp/ui"
import Select from "react-select"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { IChoiceForm } from "interfaces/xlsForm"
import { useGetCustomQuestionsQuery } from "services/household/xlsFormSchemas"

type Props = {
  form: IChoiceForm
  setForm: React.Dispatch<React.SetStateAction<IChoiceForm>>
  xlsFormId?: number
  withChoices?: boolean
}

const ChoiceListForm = ({ xlsFormId, form, setForm, withChoices = false }: Props): ReactElement => {
  const { data: questionList, isLoading: isLoadingList } = useGetCustomQuestionsQuery(
    { xlsFormId: xlsFormId ?? 0 },
    { skip: !xlsFormId },
  )

  useEffect(() => {
    if (withChoices) {
      setForm(prev => ({ ...prev, choices: [{ name: "", label: "" }] }))
    }
  }, [withChoices])

  return (
    <div className="flex flex-col gap-4">
      <TextInput
        labelText="Name"
        name="name"
        formItemClassName="text-required"
        className="custom-text-field"
        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
      />

      {withChoices && (
        <>
          <div>
            <FormLabel id="question-list" className="required">
              XLSForm field
            </FormLabel>
            <Select
              inputId="question-list"
              placeholder="Select XLSForm field"
              isLoading={isLoadingList}
              options={questionList || []}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.value?.toString()}
              isSearchable
              value={questionList?.find(item => item.value === form.fieldId)}
              onChange={item => {
                setForm(prev => ({ ...prev, fieldId: item?.value }))
              }}
              isClearable
            />
          </div>

          <div className="d-flex justify-between mt-1">
            <FormLabel className="mb-0 text-[16px]">Choices</FormLabel>
            <div
              className="d-flex wfp--label primary-color cursor-pointer text-[13px]"
              onClick={() => {
                setForm(prev => ({ ...prev, choices: [...(prev.choices || []), { name: "", label: "" }] }))
              }}
            >
              Add new choice
            </div>
          </div>

          {form.choices?.map((choice, index) => (
            <div className="flex gap-4 bg-gray-200 rounded p-4 flex-col relative" key={`choice-${index}`}>
              {index !== 0 && (
                <div
                  className="absolute -right-2 -top-2 rounded-full w-5 h-5 cursor-pointer flex justify-center items-center"
                  title="Remove choice"
                  style={{ backgroundColor: "var(--support-01)" }}
                  onClick={() =>
                    setForm(prev => ({
                      ...prev,
                      choices: prev.choices?.filter((_, idx) => idx !== index),
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faTimes} color="#fff" size="sm" />
                </div>
              )}

              <TextInput
                labelText="Name"
                name="name"
                formItemClassName="text-required"
                className="custom-text-field"
                value={choice.name}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    choices: prev.choices?.map((item, idx) =>
                      idx === index ? { ...item, name: e.target.value } : item,
                    ),
                  }))
                }
              />
              <TextArea
                labelText="Label"
                name="label"
                // @ts-ignore
                formItemClassName="text-required"
                className="custom-text-field"
                style={{ resize: "none" }}
                rows={3}
                value={choice.label}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    choices: prev.choices?.map((item, idx) =>
                      idx === index ? { ...item, label: e.target.value } : item,
                    ),
                  }))
                }
              />
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default ChoiceListForm
